import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Info } from 'lucide-react';

/**
 * SlideRating Component
 * Displays overall rating with criteria tooltip and actionable recommendations
 * 
 * Features:
 * - Overall rating score (1-10)
 * - Info button with rating criteria legend
 * - Actionable recommendations (data maintenance vs. optimization)
 * - Visual rating indicator
 */
const SlideRating = ({ 
  rating = 5, 
  ratingLabel = 'Bedingt empfehlenswert',
  summary = '',
  recommendations = [],
  criteria = {}
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Rating color based on score
  const getRatingColor = (score) => {
    if (score >= 8) return { bg: 'from-green-500 to-emerald-600', text: 'text-green-600', border: 'border-green-500' };
    if (score >= 6) return { bg: 'from-blue-500 to-indigo-600', text: 'text-blue-600', border: 'border-blue-500' };
    if (score >= 4) return { bg: 'from-yellow-500 to-orange-600', text: 'text-yellow-600', border: 'border-yellow-500' };
    return { bg: 'from-red-500 to-rose-600', text: 'text-red-600', border: 'border-red-500' };
  };

  const colors = getRatingColor(rating);

  // Default criteria if not provided
  const defaultCriteria = {
    excellent: [
      'DSCR > 1.5 (Sehr gute Schuldendienstdeckung)',
      'Eigenkapitalquote > 30%',
      'Auslastung 100%',
      'Free Cashflow > 20.000 €/Jahr',
      'Bruttorendite > 6%'
    ],
    good: [
      'DSCR > 1.2',
      'Eigenkapitalquote > 20%',
      'Auslastung > 90%',
      'Free Cashflow > 10.000 €/Jahr',
      'Bruttorendite > 4%'
    ],
    conditional: [
      'DSCR > 1.0',
      'Eigenkapitalquote > 10%',
      'Auslastung > 75%',
      'Free Cashflow > 0 €/Jahr',
      'Bruttorendite > 3%'
    ],
    poor: [
      'DSCR < 1.0 (Unzureichende Schuldendienstdeckung)',
      'Eigenkapitalquote < 10%',
      'Auslastung < 75%',
      'Free Cashflow negativ',
      'Bruttorendite < 3%'
    ]
  };

  const activeCriteria = { ...defaultCriteria, ...criteria };

  return (
    <div className="relative w-full h-full flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50 dark:from-gray-900 dark:to-slate-900 p-12 overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-600 rounded-full blur-3xl"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col">
        {/* Header with Info Button */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex items-center justify-between"
        >
          <div className="flex items-center space-x-3">
            <div className="w-1 h-12 bg-gradient-to-b from-blue-600 to-indigo-600 rounded-full"></div>
            <h2 className="text-5xl font-bold text-gray-800 dark:text-white">
              Empfehlung & Rating
            </h2>
          </div>

          {/* Info Button with Tooltip */}
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onClick={() => setShowTooltip(!showTooltip)}
              className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors duration-200"
            >
              <Info className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </button>

            {/* Tooltip */}
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 10 }}
                className="absolute right-0 top-full mt-2 w-96 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 z-50"
              >
                <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-4">Bewertungskriterien</h3>
                
                <div className="space-y-4 text-sm">
                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Sehr empfehlenswert (8-10/10)</span>
                    </div>
                    <ul className="ml-5 space-y-1 text-gray-600 dark:text-gray-400">
                      {activeCriteria.excellent.map((item, i) => (
                        <li key={i} className="text-xs">• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Empfehlenswert (6-7/10)</span>
                    </div>
                    <ul className="ml-5 space-y-1 text-gray-600 dark:text-gray-400">
                      {activeCriteria.good.map((item, i) => (
                        <li key={i} className="text-xs">• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Bedingt empfehlenswert (4-5/10)</span>
                    </div>
                    <ul className="ml-5 space-y-1 text-gray-600 dark:text-gray-400">
                      {activeCriteria.conditional.map((item, i) => (
                        <li key={i} className="text-xs">• {item}</li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">Nicht empfehlenswert (&lt;4/10)</span>
                    </div>
                    <ul className="ml-5 space-y-1 text-gray-600 dark:text-gray-400">
                      {activeCriteria.poor.map((item, i) => (
                        <li key={i} className="text-xs">• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Main Content Grid */}
        <div className="flex-1 grid grid-cols-3 gap-8">
          {/* Left: Recommendations */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="col-span-1"
          >
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Handlungsempfehlungen</h3>
            
            {recommendations.length > 0 ? (
              <div className="space-y-3">
                {recommendations.map((rec, index) => (
                  <div key={index} className="p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start space-x-2">
                      <span className={`text-lg ${rec.priority === 'high' ? 'text-red-500' : rec.priority === 'medium' ? 'text-yellow-500' : 'text-blue-500'}`}>
                        {rec.priority === 'high' ? '🚨' : rec.priority === 'medium' ? '⚡' : '💡'}
                      </span>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-800 dark:text-white">{rec.title}</p>
                        {rec.potential && (
                          <p className="text-xs text-green-600 dark:text-green-400 mt-1">Potenzial: {rec.potential}</p>
                        )}
                        {rec.type && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {rec.type === 'data' ? '📝 Datenpflege' : '📈 Optimierung'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-gray-400">Keine Handlungsempfehlungen</p>
            )}
          </motion.div>

          {/* Center: Rating Display */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="col-span-1 flex flex-col items-center justify-center"
          >
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Gesamtbewertung</p>
              <h3 className={`text-3xl font-bold ${colors.text} dark:text-white mb-4`}>
                {ratingLabel}
              </h3>
            </div>

            {/* Rating Circle */}
            <div className={`relative w-48 h-48 rounded-full bg-gradient-to-br ${colors.bg} flex items-center justify-center shadow-2xl`}>
              <div className="absolute inset-2 rounded-full bg-white dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                  <div className={`text-6xl font-bold ${colors.text}`}>{rating}</div>
                  <div className="text-lg text-gray-500 dark:text-gray-400">/10</div>
                </div>
              </div>
            </div>

            {/* Summary Text */}
            {summary && (
              <div className="mt-6 p-4 bg-white/70 dark:bg-gray-800/70 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md">
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed text-center">
                  {summary}
                </p>
              </div>
            )}
          </motion.div>

          {/* Right: Additional Recommendations */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="col-span-1"
          >
            <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-4">Verbesserungspotenziale</h3>
            
            {recommendations.filter(r => r.type === 'optimization').length > 0 ? (
              <div className="space-y-3">
                {recommendations.filter(r => r.type === 'optimization').map((rec, index) => (
                  <div key={index} className="p-3 bg-blue-50/70 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{rec.title}</p>
                    {rec.potential && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">{rec.potential}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-green-50/70 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <p className="text-sm text-green-700 dark:text-green-300">
                  ✓ Objekt ist gut optimiert. Keine wesentlichen Verbesserungspotenziale identifiziert.
                </p>
              </div>
            )}

            {/* Data Maintenance Section */}
            {recommendations.filter(r => r.type === 'data').length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold text-gray-600 dark:text-gray-400 mb-3">Datenpflege erforderlich</h4>
                <div className="space-y-2">
                  {recommendations.filter(r => r.type === 'data').map((rec, index) => (
                    <div key={index} className="p-2 bg-yellow-50/70 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
                      <p className="text-xs text-gray-700 dark:text-gray-300">📝 {rec.title}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default SlideRating;

